import { useMutation } from '@tanstack/react-query';

import { deleteEmailTemplate, createEmailTemplate, EmailTemplate, updateEmailTemplate } from 'api/emailTemplates';
import { showErrorSnackbar, showSuccessSnackbar } from 'hooks/useSnackbar';
import { queryClient } from 'providers/AppQueryClientProvider';

function useEmailTemplateMutations() {
  const createEmailTemplateMutation = useMutation({
    mutationFn: async (value: Omit<EmailTemplate, 'id'>) => await createEmailTemplate(value),
    onSuccess: () => {
      showSuccessSnackbar('New Email Template has been saved');
    },
    onError: (e) => {
      let errorMessage = 'Failed to create new Email Template';
      if (e instanceof Error) {
        errorMessage = e.message;
      }
      showErrorSnackbar(errorMessage);
      console.error(e);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
  });

  const updateEmailTemplateMutation = useMutation({
    mutationFn: async (template: EmailTemplate) => await updateEmailTemplate(template),
    onSuccess: () => {
      showSuccessSnackbar('Email Template has been updated');
    },
    onError: (e) => {
      let errorMessage = 'Failed to update new Email Template';
      if (e instanceof Error) {
        errorMessage = e.message;
      }
      showErrorSnackbar(errorMessage);
      console.error(e);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
  });

  const deleteEmailTemplateMutation = useMutation({
    mutationFn: async (id: string) => await deleteEmailTemplate(id),
    onSuccess: () => {
      showSuccessSnackbar('Email Template was successfully deleted');
    },
    onError: (err) => {
      console.error(err);
      let errorMessage = 'Failed to delete Email Template. Please try again';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      showErrorSnackbar(errorMessage);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
  });

  return { createEmailTemplateMutation, deleteEmailTemplateMutation, updateEmailTemplateMutation };
}

export { useEmailTemplateMutations };
